/**
 * What the person sends while a Code Mode turn runs — the client half of the
 * conversation inbox (src/services/conversation_inbox.py).
 *
 * A message sent mid-turn is POSTed to the inbox, not /stream. The running turn
 * reads it after its next code block and streams `inbox_delivered` naming it.
 * At turn end a message Leo read joins the thread where the saved conversation
 * keeps it (between the turn's message and its reply); one still waiting is
 * taken back from the inbox and sent on as the next ordinary message.
 *
 * Pure helpers only; the store owns the calls.
 */
import type { ExecutiveSteerOutcome } from '../../apis/executiveContract';
import { type ChatbotMessage, type InboxItem, MessageType, MessageStatus } from './types';

export type { InboxItem, InboxStatus } from './types';

export interface ServerInboxItem {
  id: string;
  message: string;
}

/** The line under a mid-turn message: the same for one Leo read live and one a reload marks read. */
export const inboxStatusLabel = (status: InboxItem['status']): string =>
  status === 'stored' ? 'Saved to Temporal — waiting for processing' : status === 'failed' ? 'Not sent' : status === 'uncertain' ? 'Not acknowledged — use Resend to keep this message’s ID' : status === 'read' ? 'Read by Leo' : status === 'queued' ? 'Queued — runs next' : 'Leo will read this at its next step';

export const queueInboxItem =(inbox: InboxItem[], key: string, message: string): InboxItem[] =>
  [...inbox, { key, message, status: 'sending' }];

export const acceptInboxItem = (inbox: InboxItem[], key: string, id: string): InboxItem[] =>
  inbox.map(item => (item.key === key ? { ...item, id, status: item.status === 'read' ? 'read' : 'pending' } : item));

export const dropInboxItem = (inbox: InboxItem[], key: string): InboxItem[] =>
  inbox.filter(item => item.key !== key);

/** An `inbox_delivered` chunk: the turn read these ids. */
export function markInboxRead(inbox: InboxItem[], ids: readonly string[]): InboxItem[] {
  const read = new Set(ids);
  return inbox.map(item => (item.id && read.has(item.id) && item.status !== 'read' ? { ...item, status: 'read' } : item));
}

/**
 * Adopt the server's pending list (GET /inbox) — on load or reattach. The
 * server owns what is still waiting: a local pending item it no longer lists
 * was taken by a turn, so Leo read it. Items the server lists that this client
 * never saw (another tab, a reload) join as pending.
 */
export function mergeServerPending(inbox: InboxItem[], pending: readonly ServerInboxItem[]): InboxItem[] {
  const waiting = new Set(pending.map(item => item.id));
  const known = new Set(inbox.map(item => item.id).filter(Boolean));
  const merged = inbox.map(item =>
    item.status === 'pending' && item.id && !waiting.has(item.id) ? { ...item, status: 'read' as const } : item,
  );
  for (const item of pending) {
    if (!known.has(item.id)) merged.push({ key: item.id, id: item.id, message: item.message, status: 'pending' });
  }
  return merged;
}

/**
 * Read items the thread already holds as saved messages. A saved turn is its
 * message, then what the person sent mid-turn, then the reply — so the saved
 * mid-turn messages are the trailing run of user messages before the final
 * reply, minus the run's first (the turn's own message). What the harness
 * appended after that reply (a returned run's receipt and Leo's line about
 * it) is not a turn, and is looked past.
 */
export function dropSavedInboxItems(inbox: InboxItem[], messages: readonly ChatbotMessage[]): InboxItem[] {
  let end = messages.length;
  while (end > 0 && (messages[end - 1].type === MessageType.ASSISTANT || messages[end - 1].harness)) end--;
  let start = end;
  while (start > 0 && messages[start - 1].type === MessageType.USER) start--;
  const saved = messages
    .slice(start + 1, end)
    .map(message => (typeof message.content === 'string' ? message.content.trim() : ''));
  return inbox.filter(item => {
    if (item.status !== 'read') return true;
    const at = saved.indexOf(item.message.trim());
    if (at === -1) return true;
    saved.splice(at, 1);
    return false;
  });
}

/**
 * Turn end, for what Leo read: each joins the thread as a user message just
 * before the turn's reply, where the saved conversation keeps it, so a later
 * reload changes nothing. Items the thread already holds are not added twice.
 */
export function foldReadInboxItems(
  messages: ChatbotMessage[],
  inbox: InboxItem[],
  toMessage: (item: InboxItem) => ChatbotMessage,
): { messages: ChatbotMessage[]; inbox: InboxItem[] } {
  const unsaved = dropSavedInboxItems(inbox, messages);
  const read = unsaved.filter(item => item.status === 'read');
  const remaining = unsaved.filter(item => item.status !== 'read');
  if (read.length === 0) return { messages, inbox: remaining };
  // Before the turn's reply, looking past a notice the harness appended after it.
  let at = messages.length;
  while (at > 0 && messages[at - 1].harness) at--;
  if (messages[at - 1]?.type === MessageType.ASSISTANT) at--;
  return {
    messages: [...messages.slice(0, at), ...read.map(toMessage), ...messages.slice(at)],
    inbox: remaining,
  };
}

/** The text a set of waiting messages is sent on as — one next message, oldest first. */
export const joinInboxMessages = (items: readonly InboxItem[]): string =>
  items.map(item => item.message.trim()).filter(Boolean).join('\n\n');

/**
 * Turn end, for what is still waiting, once each DELETE has answered.
 * `consumed` true: this client took it back, so it goes on — as the next
 * message when the turn completed, back into the composer when it was stopped
 * or failed (the person may not want it sent now). `consumed` false: a turn
 * took it first, so Leo read it. `null` (the DELETE failed): it stays queued,
 * and the next turn reads it first — never sent twice.
 */
export function planTurnEnd(
  inbox: InboxItem[],
  consumed: ReadonlyMap<string, boolean | null>,
  completed: boolean,
): { inbox: InboxItem[]; send: string; restore: string } {
  const taken = inbox.filter(item => consumed.get(item.key) === true);
  const next = inbox
    .filter(item => consumed.get(item.key) !== true)
    .map(item => (consumed.get(item.key) === false ? { ...item, status: 'read' as const } : item));
  const text = joinInboxMessages(taken);
  return { inbox: next, send: completed ? text : '', restore: completed ? '' : text };
}

/**
 * Executive turn end. The workflow, not the Redis inbox, owns an executive
 * steer: what the turn did not read was reclaimed into workflow state by
 * close_turn and runs next. So nothing is taken back or sent on from here —
 * that would deliver it twice — it is only relabelled queued.
 */
export const queueExecutiveLeftovers = (inbox: InboxItem[]): InboxItem[] =>
  inbox.map(item => (item.status === 'pending' && item.id ? { ...item, status: 'queued' as const } : item));

/**
 * A poll of the executive's workflow: a queued steer it no longer lists was
 * taken by an activation. It leaves the inbox and joins the thread (until the
 * saved transcript replaces it). Unchanged input is returned as-is.
 */
export function takeDequeuedSteers(
  inbox: InboxItem[],
  queuedIds: readonly string[],
  outcomes?: Record<string, ExecutiveSteerOutcome>,
): { inbox: InboxItem[]; taken: InboxItem[] } {
  const waiting = new Set(queuedIds);
  const taken: InboxItem[] = [];
  const next = inbox.flatMap(item => {
    const receipt = item.id ? outcomes?.[item.id] : undefined;
    if (receipt?.status === 'closed' || receipt?.status === 'read') {
      taken.push(item);
      return [];
    }
    if (receipt?.status === 'rejected') return [{ ...item, status: 'failed' as const }];
    if (item.status === 'read') return [item];
    if (receipt?.status === 'landed') return [{ ...item, status: 'pending' as const }];
    if (receipt?.status === 'queued' || (item.status === 'stored' && item.id && waiting.has(item.id))) {
      return [{ ...item, status: 'queued' as const }];
    }
    // Stored signals need an explicit result, even after queue admission.
    // Missing/expired/unknown results cannot authorize folding or resubmission.
    if (!item.durableReceipt && outcomes === undefined && item.status === 'queued' && item.id && !waiting.has(item.id)) {
      taken.push(item);
      return [];
    }
    return [item];
  });
  return { inbox: next.every((item, index) => item === inbox[index]) && next.length === inbox.length ? inbox : next, taken };
}

/** Match executive delivery by identity only, including coalesced opening messages. */
export function dropSavedExecutiveInbox(inbox: InboxItem[], messages: readonly ChatbotMessage[]): InboxItem[] {
  const userMessages = messages.filter(message => message.type === MessageType.USER && !message.harness);
  const msgIds = new Set(userMessages.flatMap(message => message.msgIds ?? []));
  const sendIds = new Set(userMessages.flatMap(message => message.sendIds ?? []));
  return inbox.filter(item => !(item.id && msgIds.has(item.id)) && !sendIds.has(item.key));
}

/** A workflow/read receipt may beat history, but may never append a second bubble for a saved send. */
export function foldExecutiveSteers(messages: ChatbotMessage[], taken: InboxItem[], timestamp: number): ChatbotMessage[] {
  const result = [...messages];
  for (const item of taken) {
    if (!dropSavedExecutiveInbox([item], result).length) continue;
    result.push({
      id: `steer-${item.id ?? item.key}`, type: MessageType.USER, content: item.message,
      timestamp, status: MessageStatus.SENT, inboxStatus: 'read', pendingHistory: true,
      msgIds: item.id ? [item.id] : [], sendIds: [item.key],
    });
  }
  return result;
}

/** A stale snapshot cannot erase a receipt; a newer saved message replaces it, even for a group. */
export function mergeExecutiveHistory(current: ChatbotMessage[], saved: ChatbotMessage[]): ChatbotMessage[] {
  const result = [...saved];
  for (const message of current) {
    if (!message.pendingHistory) continue;
    const identity = { key: message.sendIds?.[0] ?? '', id: message.msgIds?.[0], message: '', status: 'read' as const };
    if (dropSavedExecutiveInbox([identity], result).length) result.push(message);
  }
  return result;
}

/** Reuse the existing inbox identity only for a manual resend of unacknowledged text. */
export function prepareExecutiveSend(inbox: InboxItem[], message: string, mintId: () => string, resendKey?: string): { key: string; inbox: InboxItem[] } {
  const previous = inbox.find(item => item.status === 'uncertain' && item.key === resendKey && item.message === message);
  if (resendKey && !previous) throw new Error('This message is no longer awaiting a resend.');
  const key = previous?.key ?? mintId();
  return { key, inbox: queueInboxItem(dropInboxItem(inbox, key), key, message) };
}

/** A failed acknowledgment does not prove non-delivery. Keep its id until a manual resend. */
export function retainUncertainExecutiveSend(inbox: InboxItem[], key: string): InboxItem[] {
  return inbox.map(item => item.key === key ? { ...item, status: 'uncertain' as const } : item);
}

/** A duplicate acknowledges the original send; it must not become another queued message. */
export function acknowledgeExecutiveSend(inbox: InboxItem[], key: string, id: string, mode: 'stored' | 'queued' | 'landed_in_turn' | 'duplicate'): InboxItem[] {
  if (mode === 'duplicate') return dropInboxItem(inbox, key);
  const accepted = acceptInboxItem(inbox, key, id);
  return mode === 'stored'
    ? accepted.map(item => item.key === key && item.status !== 'read' ? { ...item, status: 'stored' as const, durableReceipt: true } : item)
    : mode === 'queued'
    ? accepted.map(item => item.key === key ? { ...item, status: 'queued' as const } : item)
    : accepted;
}

/** Accepted Executive ingress still waiting for an authoritative read or result. */
export const hasPendingExecutiveInbox = (inbox: readonly InboxItem[]): boolean =>
  inbox.some(item => item.status === 'sending' || item.status === 'stored' ||
    item.status === 'pending' || item.status === 'queued');
